function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TableList, Page, PageEmptyState, PageErrorState, FilterPickerGroup, Button, Typography, Searchbox, PageContentHeader, ConfirmationModal, DropdownMenu, ChipButton, useStateInRef, DeclarativeModal } from 'seagull';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import { useGlobalContext, useStore, useStoreActions } from "../../contexts";
import { useSortFromUrl } from "../../hooks/useSortFromUrl";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { useDebouncedLogSearchToBI } from "../../hooks/useDebouncedLogSearchToBI";
import { CreateProposalButton } from "../../components/CreateProposalButton";
import PageLoadingState from "../../components/PageLoadingState";
import { selectors } from "../../store/state-selectors";
import ProposalsDashboardRow from "./ProposalsDashboardRow";
import { ProposalsIllustration } from "./ProposalsIllustration";
import { sortFunctions, getStatusUIData, getProposalsStatusCounters } from "./ProposalsDashboard.utils";
import { ProposalsProgressStatusFilterPicker } from "./ProposalsProgressStatusFilterPicker";
import SeeWhatItsLike from "../../components/SeeWhatItsLike/SeeWhatItsLike";
import img1 from "./SeeWhatItsLikeImages/1.png";
import img2 from "./SeeWhatItsLikeImages/2.png";
import img3 from "./SeeWhatItsLikeImages/3.png";
import img4 from "./SeeWhatItsLikeImages/4.png";
import { ProposalsSentByFilterPicker } from "./ProposalsSentByFilterPicker/ProposalsSentByFilterPicker";
import ProposalStatusLog from "./ProposalStatusLogModal/ProposalStatusLog";
import { relationshipSummaryModel } from "../../models";
import ExternalLinkButton from "../../components/ExternalLinkButton";
import countBy from 'lodash/countBy';
import isEmpty from 'lodash/isEmpty';
import mapValues from 'lodash/mapValues';
import { MailSentLiner24, WideArrowDownward, WideArrowUpward } from 'seagull/icons';
import styles from "./ProposalsDashboard.module.scss";
import { useBulkActionModal } from "../../hooks/useTerminateAgreement/useBulkActionModal";
import { handlePromise, numberAndUnit } from "../../infra/utils";
const vendorTitle = 'Sent Proposals';
const clientTitle = 'Proposals';
export let HeaderColId;

(function (HeaderColId) {
  HeaderColId["CompanyName"] = "companyName";
  HeaderColId["SentDate"] = "sentDate";
  HeaderColId["SentBy"] = "sentBy";
  HeaderColId["Status"] = "status";
})(HeaderColId || (HeaderColId = {}));

const getHeaderItems = () => {
  return [{
    id: HeaderColId.CompanyName,
    value: 'Company name'
  }, {
    id: HeaderColId.SentDate,
    value: 'Sent date'
  }, {
    id: HeaderColId.SentBy,
    value: 'Sent by'
  }, {
    id: HeaderColId.Status,
    value: 'Status'
  }].filter(Boolean);
};

const getColumnDefs = () => {
  return [{
    width: [300, 370],
    sortable: true
  }, // Company name
  {
    width: [90, 110],
    sortable: true
  }, // Sent date
  {
    width: [150, 180],
    sortable: true
  }, // Sent by
  {
    width: [230, 300],
    sortable: true,
    sortOrder: ['asc', 'desc']
  } // Status
  ].filter(Boolean);
};

const defaultSort = {
  colIndex: 1,
  sort: 'desc'
};
export function ActualProposalsDashboard(props) {
  const {
    data,
    isRefreshing,
    refresh
  } = props;
  const isVendor = useStore(selectors.user.isVendor);
  const globalContext = useGlobalContext();
  const {
    api,
    logBI
  } = useStoreActions();
  const scrollElRef = useRef(null);
  const columnDefs = useMemo(() => getColumnDefs(), []);
  const headerItems = useMemo(() => getHeaderItems(), []);
  const sortIds = useMemo(() => headerItems.map(item => item.id), [headerItems]);
  const {
    sort,
    changeSort
  } = useSortFromUrl({
    defaultSort,
    sortIds
  });
  const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11303);
  const {
    urlState: {
      filter,
      search,
      sentBy,
      review
    },
    changeUrlState
  } = useStateFromUrl({
    filter: {
      multi: true,
      defaultValue: []
    },
    search: {
      defaultValue: ''
    },
    // people names may contain commas, so we use a custom query parameter delimeter
    sentBy: {
      multi: true,
      defaultValue: [],
      delimeter: '|'
    },
    review: {
      defaultValue: ''
    }
  });
  const {
    doBulkAction
  } = useBulkActionModal();
  const sortedAndFiltered = useMemo(() => {
    const textSearchProposal = (search, proposal) => {
      return "".concat(relationshipSummaryModel.opposingPartyName(proposal, isVendor), " ").concat(proposal.title).toLowerCase().includes(search);
    };

    const filtered = data.filter(proposal => {
      const passesStatusFilter = filter.length === 0 || filter.includes(getStatusUIData(proposal, isVendor).viewableStatus);
      const passesSentByFilter = sentBy.length === 0 || sentBy.includes(proposal.seller.id);
      const normalizedSearch = search ? search.trim().toLowerCase() : search;
      const passesSearch = !normalizedSearch || textSearchProposal(normalizedSearch, proposal);
      return passesStatusFilter && passesSentByFilter && passesSearch;
    });

    if (sort) {
      filtered.sort(sortFunctions[sort.colIndex]({
        isVendor,
        isAscending: sort.sort === 'asc'
      }));
    }

    return filtered;
  }, [data, isVendor, sort, filter, search, sentBy]);
  const [selected, setSelected] = useState({});
  const getSelected = useStateInRef(selected);
  const [bulkActionsOpen, setBulkActionsOpen] = useState(false);
  const numSelected = useMemo(() => {
    var _countBy$true;

    return (_countBy$true = countBy(selected).true) !== null && _countBy$true !== void 0 ? _countBy$true : 0;
  }, [selected]);
  const agreementInReview = useMemo(() => {
    if (!isVendor) return;
    return review ? sortedAndFiltered.find(a => a.id === review) : undefined;
  }, [sortedAndFiltered, isVendor, review]);
  const {
    paginatedData,
    onScroll
  } = useInfiniteScroll({
    data: sortedAndFiltered,
    scrollElRef
  }); //TODO - counters should be received as a separate API

  const counters = useMemo(() => getProposalsStatusCounters(data, isVendor), [data, isVendor]);

  const onSortChange = sort => {
    logBI(11300, {
      column: sort ? headerItems[sort.colIndex].id : 'none'
    });
    changeSort(sort);
  };

  const resetFilters = () => {
    changeUrlState({
      filter: [],
      search: '',
      sentBy: []
    });
  };

  const onOpenReview = proposal => changeUrlState({
    review: proposal.id
  });

  useEffect(() => {
    // this unselects any agreement that is not found in the newly fetched list of agreements
    if (!data || isEmpty(getSelected())) return;
    const ids = data.map(proposal => proposal.id);
    const newSelected = mapValues(getSelected(), (origValue, proposalId) => ids.includes(proposalId) ? origValue : false);
    setSelected(newSelected);
  }, [data]);

  function getSelectedProposals() {
    return Object.entries(selected).filter(_ref => {
      let [, isSelected] = _ref;
      return isSelected;
    }).map(_ref2 => {
      let [proposalId, _] = _ref2;
      return proposalId;
    });
  }

  const onToggleAllSelection = useCallback(selectAll => {
    setSelected(sortedAndFiltered.reduce((acc, _ref3) => {
      let {
        id
      } = _ref3;
      return Object.assign(acc, {
        [id]: selectAll
      });
    }, {}));
  }, [sortedAndFiltered]);

  if (!data.length) {
    return /*#__PURE__*/React.createElement(Page, {
      header: isVendor ? vendorTitle : clientTitle,
      loading: isRefreshing
    }, /*#__PURE__*/React.createElement(PageEmptyState, {
      "data-testid": "proposals-dashboard-empty",
      illustration: /*#__PURE__*/React.createElement(ProposalsIllustration, null),
      title: isVendor && 'Create proposals in a matter of minutes',
      message: isVendor ? 'Our proposals are designed for a fast setup with smart fields, a service library, and default settings.' : 'Here you will see proposals pending your approval',
      actions: isVendor && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SeeWhatItsLike, {
        "data-testid": "proposals-dashboard",
        steps: [{
          title: 'Standardized proposal creation',
          subtitle: 'One proposal creation process for all of your services with easy client searching, a testing option, and a service library.',
          image: {
            src: img1,
            width: 1246,
            height: 748
          }
        }, {
          title: 'Productized services, made easy',
          subtitle: 'Break down your offerings into service modules that can be mixed up for different client cases and easily added to your agreements.',
          image: {
            src: img2,
            width: 1246,
            height: 748
          }
        }, {
          title: 'Get proposals approved quickly!',
          subtitle: 'Show your clients a branded, clear breakdown of your services with pricing so they can easily understand what they need to approve.',
          image: {
            src: img3,
            width: 1198,
            height: 700
          }
        }, {
          title: 'Tracking your proposals made simple',
          subtitle: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
            skin: "invertedStandard"
          }, "Keep track of the proposal status, edit, and remind a client to approve \u2014 all in one place!"), /*#__PURE__*/React.createElement(Typography, {
            skin: "invertedStandard"
          }, "We will support you with automatic reminders as well.")),
          image: {
            src: img4,
            width: 1246,
            height: 748
          }
        }]
      }), /*#__PURE__*/React.createElement(CreateProposalButton, {
        text: "Create a new proposal",
        hidePrefix: true,
        "data-testid": "agreements-empty-new-btn"
      }))
    }));
  }

  const openImportProposalsModal = () => {
    globalContext.showModal({
      content: _ref4 => {
        let {
          dismissModal
        } = _ref4;
        return /*#__PURE__*/React.createElement(ConfirmationModal, {
          textAlignCenter: false,
          primaryButtonLabel: "Close",
          onPrimaryButtonClick: dismissModal,
          onCloseButtonClick: dismissModal,
          title: "Import proposals",
          primaryButtonTestid: "close-import-proposals-modal",
          content: /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography, null, "Need to send multiple proposals and speed up creation process?"), /*#__PURE__*/React.createElement(Typography, null, /*#__PURE__*/React.createElement(ExternalLinkButton, {
            openInNewTab: true,
            href: "https://forms.gle/Q9sMHXFcp5Fs866FA"
          }, "Complete this form"), ' ', "and our team will reach out with the next steps."), /*#__PURE__*/React.createElement(Typography, {
            variant: "paragraph2",
            style: {
              marginTop: '16px'
            }
          }, "Please note: This service is currently available for requests of 30 proposals or more."))
        });
      }
    });
  };

  async function bulkSendReminders(selectedProposals) {
    await doBulkAction({
      bulkItemIds: selectedProposals,
      confirmationTitle: 'Send reminders in bulk',
      confirmationPrimaryButtonLabel: 'Send reminders',
      confirmationContent: /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography, null, "By selecting \"Send reminders\", reminders will be sent to all clients of the", ' ', /*#__PURE__*/React.createElement("strong", null, selectedProposals.length), " selected proposals.")),
      progressTitle: 'Send reminders in bulk',
      actionName: 'sent',
      itemName: 'reminder',
      onConfirmed: () => {
        logBI(11255, {
          count: selectedProposals.length
        });
      },
      canProcessItem: () => true,
      processSingleItem: proposalId => handlePromise(api.relationship.resendApprovalEmail(proposalId)).then(_ref5 => {
        let [result, _error, isError] = _ref5;
        return [result, null, isError];
      }),
      onDone: failedProposalIds => {
        setSelected(failedProposalIds.reduce((acc, proposalId) => _objectSpread(_objectSpread({}, acc), {}, {
          [proposalId]: true
        }), {}));
      }
    });
  }

  return /*#__PURE__*/React.createElement(Page, {
    "data-testid": "proposals-dashboard",
    ref: scrollElRef,
    header: isVendor ? vendorTitle : clientTitle,
    actions: isVendor && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
      skin: "primaryOutline",
      "data-testid": "import-proposals-btn",
      onClick: () => openImportProposalsModal()
    }, "Import"), /*#__PURE__*/React.createElement(CreateProposalButton, null)),
    subheader: "".concat(numberAndUnit(data.length, 'proposal'), " awaiting approval"),
    loading: isRefreshing,
    onScroll: onScroll
  }, /*#__PURE__*/React.createElement(TableList.Provider, {
    hasActionColumn: true,
    columnDefs: columnDefs,
    sort: sort,
    onSortChange: onSortChange,
    enableRowSelection: isVendor,
    selectedRows: selected,
    onSelectionChange: setSelected,
    numRows: sortedAndFiltered.length,
    numSelectedRows: numSelected,
    onToggleAllSelection: onToggleAllSelection
  }, /*#__PURE__*/React.createElement(PageContentHeader, null, /*#__PURE__*/React.createElement("div", {
    className: styles.filtersRow
  }, /*#__PURE__*/React.createElement(FilterPickerGroup, {
    "data-testid": "proposals-dashboard-filter-picker-group",
    onClearClick: resetFilters,
    disableClear: !filter.length && !search && !sentBy.length
  }, /*#__PURE__*/React.createElement(Searchbox, {
    registerHotkey: true,
    value: search,
    "data-testid": "proposals-dashboard-search",
    onChange: event => {
      const searchStr = event.target.value;
      changeUrlState({
        search: searchStr
      });
      debouncedLogSearchToBI.onChange(searchStr);
    },
    onClear: () => {
      changeUrlState({
        search: ''
      });
      debouncedLogSearchToBI.onClear();
    }
  }), /*#__PURE__*/React.createElement(ProposalsProgressStatusFilterPicker, {
    isVendor: isVendor,
    value: filter,
    onChange: filter => changeUrlState({
      filter
    }),
    counters: counters
  }), /*#__PURE__*/React.createElement(ProposalsSentByFilterPicker, {
    value: sentBy,
    onChange: sentBy => changeUrlState({
      sentBy
    }),
    possibleValues: data.reduce((acc, _ref6) => {
      let {
        seller: {
          id,
          firstName,
          lastName
        }
      } = _ref6;
      return firstName != null && lastName != null ? Object.assign(acc, {
        [id]: "".concat(firstName, " ").concat(lastName)
      }) : acc;
    }, {})
  })), isVendor && /*#__PURE__*/React.createElement("div", {
    className: styles.bulkActions
  }, /*#__PURE__*/React.createElement(Typography, {
    "data-testid": "agreement-dashboard-bulk-counter"
  }, numSelected > 0 ? "".concat(numberAndUnit(numSelected, 'proposal'), " selected") : ''), /*#__PURE__*/React.createElement(DropdownMenu, {
    onOpenChange: setBulkActionsOpen,
    target: /*#__PURE__*/React.createElement(ChipButton, {
      "data-testid": "proposals-dashboard-bulk-actions",
      suffixIcon: bulkActionsOpen ? /*#__PURE__*/React.createElement(WideArrowUpward, null) : /*#__PURE__*/React.createElement(WideArrowDownward, null),
      highlight: bulkActionsOpen,
      disabled: numSelected === 0
    }, "Bulk actions")
  }, /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    iconStart: /*#__PURE__*/React.createElement(MailSentLiner24, null),
    "data-testid": "proposals-dashboard-bulk-send-reminders",
    onClick: () => bulkSendReminders(getSelectedProposals()),
    disabled: !isVendor
  }, "Send reminders")))), /*#__PURE__*/React.createElement(TableList.Header, {
    items: headerItems,
    "data-testid": "proposals-dashboard-table-header"
  })), sortedAndFiltered.length > 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DeclarativeModal, {
    closeOnClickOutside: true,
    shouldBlurBackground: true,
    closeOnEscapeKey: true,
    isOpen: !!agreementInReview,
    onClose: () => changeUrlState({
      review: ''
    }),
    content: p => /*#__PURE__*/React.createElement(ProposalStatusLog, _extends({
      relationshipSummary: agreementInReview
    }, p))
  }), paginatedData.map((proposal, index) => /*#__PURE__*/React.createElement(ProposalsDashboardRow, {
    key: proposal.id,
    agreement: proposal,
    isVendor: isVendor,
    index: index,
    refresh: refresh,
    onOpenReview: onOpenReview
  }))) : /*#__PURE__*/React.createElement(PageEmptyState, {
    illustration: /*#__PURE__*/React.createElement(ProposalsIllustration, null),
    title: "No Proposals match your request",
    message: "Try adjusting your search by removing filters.",
    actions: /*#__PURE__*/React.createElement(Button, {
      onClick: resetFilters
    }, "Remove all filters")
  })));
}
export function ProposalsDashboard() {
  const {
    data,
    loading,
    error
  } = useStore(state => state.proposals);
  const isVendor = useStore(selectors.user.isVendor);
  const {
    fetchProposals
  } = useStoreActions();
  useEffect(() => {
    document.title = isVendor ? vendorTitle : clientTitle;
    fetchProposals();
  }, [isVendor]);

  if (error) {
    return /*#__PURE__*/React.createElement(PageErrorState, {
      title: isVendor ? vendorTitle : clientTitle,
      onRefresh: fetchProposals
    });
  } else if (!data) {
    return /*#__PURE__*/React.createElement(PageLoadingState, {
      header: isVendor ? vendorTitle : clientTitle,
      loading: loading
    });
  } else {
    return /*#__PURE__*/React.createElement(ActualProposalsDashboard, {
      data: data,
      isRefreshing: loading,
      refresh: fetchProposals
    });
  }
}